import { useSelector } from 'react-redux';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import useAnalyzeRoute from '../../hooks/useAnalyzeRoute';
import { selectLoginStatus } from '../../redux/modules/authSlice';
import PrivateLayout from '../private/layouts/PrivateLayout';
import PublicLayout from '../public/layouts/PublicLayout';
import LoginScreen from '../public/screens/LoginScreen';
import HomeScreen from '../private/screens/home/HomeScreen';
import MainRoutes from './MainRoutes';
import SearchResultsScreen from '../private/screens/SearchResultsScreen';
import NewsListScreen from '../private/screens/news/NewsListScreen';
import NewsDetailScreen from '../private/screens/news/NewsDetailScreen';
import RegisterScreen from '../public/screens/RegisterScreen';
import LoadingScreen from '../misc/LoadingScreen';
import RecentlyViewedScreen from '../private/screens/RecentlyViewedScreen';
import FavoritesScreen from '../private/screens/favorites/FavoritesScreen';
import FavoriteListScreen from '../private/screens/favorites/FavoriteListScreen';
import SetDocumentTitle from './SetDocumentTitle';
import ForgotPasswordScreen from '../public/screens/ForgotPasswordScreen';
import Tracking from '../Tracking';
import EvaluationsScreen from '../private/screens/evaluations/EvaluationsScreen';
import EvaluationsForm from '../private/screens/evaluations/EvaluationForm';

const Router = () => {
	const loginStatus = useSelector((state) => selectLoginStatus(state));
	return (
		<>
			<BrowserRouter basename={process.env.REACT_APP_ROUTER_BASENAME}>
				<Tracking />
				<Switch>
					{loginStatus !== 'success' && (
						<Route exact path="/login">
							<SetDocumentTitle type="login" />
							<PublicLayout><LoginScreen /></PublicLayout>
						</Route>
					)}
					{loginStatus !== 'success' && (
						<Route exact path="/register">
							<SetDocumentTitle type="register" />
							<PublicLayout><RegisterScreen /></PublicLayout>
						</Route>
					)}
					{loginStatus !== 'success' && (
						<Route exact path="/forgot-password">
							<SetDocumentTitle type="forgot-password" />
							<PublicLayout><ForgotPasswordScreen /></PublicLayout>
						</Route>
					)}
					{loginStatus !== 'success' && (
						<Route exact path="*">
							<SetDocumentTitle type="login" />
							<PublicLayout><Redirect to="/login" /></PublicLayout>
						</Route>
					)}

					<Route exact path={['/logout']}>
						<SetDocumentTitle type="loading" />
						<LoadingScreen />
					</Route>

					<Route exact path={['/news']}>
						<SetDocumentTitle type="newsList" />
						<NewsListScreen />
					</Route>

					<Route exact path={['/news/:newsId']}>
						<SetDocumentTitle type="newsDetail" />
						<NewsDetailScreen />
					</Route>

					<Route exact path={['/recently-viewed']}>
						<SetDocumentTitle type="recentlyViewed" />
						<RecentlyViewedScreen />
					</Route>

					<Route exact path={['/favorites']}>
						<SetDocumentTitle type="favorites" />
						<FavoritesScreen />
					</Route>

					<Route exact path={['/favorites/:favoriteListId']}>
						<SetDocumentTitle type="favoriteList" />
						<FavoriteListScreen />
					</Route>

					<Route exact path={['/:baId/evaluations']}>
						<SetDocumentTitle type="evaluations" />
						<EvaluationsScreen />
					</Route>

					<Route exact path={['/:baId/evaluations/:id']}>
						<SetDocumentTitle type="evaluationForm" />
						<EvaluationsForm />
					</Route>

					<Route path={[
						'/search/:searchString',
						'/search',
					]}
					>
						<SearchResultsScreen />
					</Route>

					<Route path={[
						'/:baId/:folder1Id/:folder2Id/:folder3Id/in-:propertyId',
						'/:baId/:folder1Id/:folder2Id/in-:propertyId',
						'/:baId/:folder1Id/:folder2Id/:folder3Id',
						'/:baId/:folder1Id/:folder2Id/',
						'/:baId',
						'/',
					]}
					>
						<MainRouter />
					</Route>

					<Route path="*">
						<Redirect to="/" />
					</Route>
				</Switch>
			</BrowserRouter>
		</>
	);
};

export default Router;

const MainRouter = () => {
	const { redirectPath, activeTree } = useAnalyzeRoute();

	if (redirectPath) {
		return <Redirect to={redirectPath} />;
	}

	return (
		<Switch>
			<Route exact path="/">
				<PrivateLayout activeTree={activeTree}>
					<SetDocumentTitle type="home" />
					<HomeScreen />
				</PrivateLayout>
			</Route>
			<Route
				exact
				path="/:baId/:folder1Id"
				render={({ match }) => (
					<Redirect to={`/${match.params.baId}`} />
				)}
			/>
			<Route path={[
				'/:baId/:folder1Id/:folder2Id/:folder3Id/in-:propertyId',
				'/:baId/:folder1Id/:folder2Id/in-:propertyId',
				'/:baId/:folder1Id/:folder2Id/:folder3Id',
				'/:baId/:folder1Id/:folder2Id/',
				'/:baId',
			]}
			>
				<PrivateLayout activeTree={activeTree}>
					<MainRoutes activeTree={activeTree} />
				</PrivateLayout>
			</Route>
		</Switch>
	);
};
